/**
 * Core-100 R4 wave 3 (X4) — CORE-AE D/S: "Actions taken here SHALL pass through the same governed paths as the
 * equivalent action anywhere else. A detail surface MUST NOT be a back door."
 *
 * The Command Center's two edit modals used to UPDATE `tickets` straight from the browser. They now write through
 * the server's governed paths (migration 20270206024700):
 *   - `edit_ticket_details`          — type, comeback, Waiter time (scheduled_start_time / start_time), Due Out.
 *                                      Front office only; a Due Out change needs a reason from the closed vocabulary
 *                                      and is audited against the SERVER's old value.
 *   - `edit_ticket_customer_contact` — customer name / phone / email, messaging consent and channels, odometer, GVWR.
 *                                      Front office only; audited by field name (never the values).
 * Both are SECURITY INVOKER, so RLS and every tickets trigger still apply. Anything else is refused by the server
 * (EDIT_FIELD_NOT_ALLOWED), so this module does not re-check the field list.
 */
import { supabase } from '@/integrations/supabase/client';

export const TICKET_DETAILS_FIELDS = ['type', 'is_comeback', 'scheduled_start_time', 'start_time', 'due_out'] as const;
export const TICKET_CUSTOMER_CONTACT_FIELDS = [
  'customer_name', 'customer_phone', 'customer_email', 'email_opt_in', 'sms_opt_in',
  'preferred_channel', 'fallback_channel', 'odometer_miles', 'gvwr_lbs', 'updated_at',
] as const;

export interface DueOutEditStatement {
  reason: string;
  reason_text?: string | null;
  customer_communication_approved?: boolean | null;
}

/** What the server recorded for a Due Out change (null when the Due Out did not change). */
export interface ServerDueOutEdit {
  ticket_id: string;
  old_due_out: string | null;
  new_due_out: string | null;
  kind: string;
  reason: string;
  reason_text: string | null;
  actor_user_id: string;
  edited_at: string;
  customer_communication_approved: boolean | null;
}

/** `ok` false carries the server's refusal in `error` (e.g. DUE_OUT_REASON_REQUIRED, EDIT_FRONT_OFFICE_ONLY). */
export interface GovernedEditResult {
  ok: boolean;
  applied: string[];
  dueOutEdit: ServerDueOutEdit | null;
  error?: string;
}

function refusal(error: { message?: string } | null | undefined, fallback: string): GovernedEditResult {
  return { ok: false, applied: [], dueOutEdit: null, error: error?.message || fallback };
}

export async function editTicketDetails(
  ticketId: string,
  patch: Record<string, unknown>,
  dueOutEdit?: DueOutEditStatement | null,
): Promise<GovernedEditResult> {
  const { data, error } = await supabase.rpc('edit_ticket_details' as never, {
    p_ticket_id: ticketId,
    p_patch: patch,
    p_due_out_edit: dueOutEdit ?? null,
  } as never);
  if (error) return refusal(error, 'Failed to update ticket details');
  const r = (data ?? {}) as { success?: boolean; applied?: string[]; due_out_edit?: ServerDueOutEdit | null };
  if (r.success !== true) return refusal(null, 'Failed to update ticket details');
  return { ok: true, applied: r.applied ?? [], dueOutEdit: r.due_out_edit ?? null };
}

export async function editTicketCustomerContact(
  ticketId: string,
  patch: Record<string, unknown>,
): Promise<GovernedEditResult> {
  const { data, error } = await supabase.rpc('edit_ticket_customer_contact' as never, {
    p_ticket_id: ticketId,
    p_patch: patch,
  } as never);
  if (error) return refusal(error, 'Failed to save customer details');
  const r = (data ?? {}) as { success?: boolean; applied?: string[] };
  if (r.success !== true) return refusal(null, 'Failed to save customer details');
  return { ok: true, applied: r.applied ?? [], dueOutEdit: null };
}
